<template lang="pug">
.team-list
    //- A refusal has to LOOK like a refusal. Left to the empty state, somebody
    //- without the right was told "No records found", which reads as "the team
    //- is empty" rather than "this is not yours to see".
    .error(v-if="loadError")
        Error(:message="loadError.message" :code="loadError.code")
    paginated-items(
        v-else
        scope="ex"
        :type="paginationType"
        :loading="loading"
        :items="teamPaginated.items"
        :show-filters="showFilters"
        :filters="teamPaginated.filters"
        :variables="teamPaginated.variables"
        :all-columns="teamPaginated.allColumns"
        :active-columns="teamPaginated.activeColumns"
        :page-info="teamPaginated.pageInfo"
        :sort="sort"
        @pagination-size-change="handleSizeChange"
        @pagination-current-change="handleCurrentChange"
        @show-table-columns="handleColsShow"
        @change-filter="handleFilterInputChange"
        @click="handleClick"
        @change-sort="handleSortInputChange"
    )
        div(slot="top")
            el-row.table-top(v-loading="loading" type="flex" justify="space-between")
                .left
                    //- The only way onto the platform team. Without it,
                    //- removing somebody was a one-way door: this list shows
                    //- super users only, so there was nothing left to promote
                    //- them back from.
                    el-button(
                        v-if="mayManageSuperUsers"
                        type="primary"
                        size="mini"
                        icon="el-icon-fa-plus fas"
                        @click="openPromoteDialog"
                    ) {{ $t('Add super user') }}
                    //- The audit trail of who entered whose account, and the
                    //- way to end a session from the outside. Same right as
                    //- managing the team: it is the same trust.
                    router-link(v-if="isSystemScope && mayManageSuperUsers" :to="{ name: 'system-team-impersonation-sessions' }")
                        el-button(size="mini" icon="el-icon-fa-user-secret fas") {{ $t('Support sessions') }}
                    //- Who is away when. Every super user, not a right.
                    router-link(v-if="isSystemScope" :to="{ name: 'system-team-absences' }")
                        el-button(size="mini" icon="el-icon-fa-umbrella-beach fas") {{ $t('Absences') }}
                    timespan-picker(v-model="span")
                .right.hide-on-empty
                    el-button(v-if="hasFilters" type="text" size="mini" @click="clearFilters") {{ $t('Reset filters') }}
                    el-button(
                        plain
                        size="mini"
                        type="primary"
                        icon="el-icon-fa-filter fas"
                        @click="toggleFilterVisiblity"
                    )
                        span(v-if="showFilters") {{ $t('Hide filters') }}
                        span(v-else) {{ $t('Show filters') }}

        template(v-slot:name="{ item }")
            span {{ item.first_name }} {{ item.last_name }}

        template(v-slot:email="{ item }")
            span.muted {{ item.email }}

        template(v-slot:logged_time="{ item }")
            span {{ formatSupportTime(item.supportTime.logged_seconds) }}

        template(v-slot:billable_time="{ item }")
            span {{ formatMinutes(item.supportTime.billable_minutes) }}

        template(v-slot:entries="{ item }")
            span {{ item.supportTime.entry_count }}

        //- Only ever a number worth reading when it is not zero.
        template(v-slot:to_check="{ item }")
            el-tag(v-if="item.supportTime.to_check_count" size="mini" type="warning") {{ item.supportTime.to_check_count }}
            span.muted(v-else) 0

        template(v-slot:active_days="{ item }")
            span {{ item.supportTime.active_days }}

    el-dialog.add-super-user-dialog(
        :title="$t('Add super user')"
        :visible.sync="promote.visible"
        width="480px"
        append-to-body
    )
        el-form(label-position="top" size="small" @submit.native.prevent)
            el-form-item(:label="$t('Person')" :error="promote.error")
                el-select(
                    v-model="promote.userId"
                    filterable
                    remote
                    clearable
                    style="width: 100%"
                    :remote-method="searchCandidates"
                    :loading="promote.searching"
                    :placeholder="$t('Search by name or email...')"
                )
                    el-option(
                        v-for="item in promote.candidates"
                        :key="item.id"
                        :label="candidateLabel(item)"
                        :value="item.id"
                    )
                p.help {{ $t('Gets the platform area and every platform right. Remove what they should not have on their detail page.') }}
        //- Same footer every other dialog here uses: not round, icon + label,
        //- Element's own spacing between them.
        template(slot="footer")
            el-button(
                size="mini"
                icon="el-icon-fa-times fas"
                @click="promote.visible = false"
            ) {{ $t('Dismiss') }}
            el-button(
                size="mini"
                type="primary"
                icon="el-icon-fa-check fas"
                :loading="promote.saving"
                @click="confirmPromote"
            ) {{ $t('Add super user') }}
</template>

<i18n>
{
    "de": {
        "Support sessions": "Support-Sitzungen",
        "Absences": "Abwesenheiten",
        "Reset filters": "Filter zurücksetzen",
        "Show filters": "Filter anzeigen",
        "Hide filters": "Filter verbergen",
        "The team could not be loaded.": "Das Team konnte nicht geladen werden.",
        "You are not allowed to see this overview.": "Für diese Übersicht fehlt die Berechtigung.",
        "Add super user": "Super User hinzufügen",
        "Choose a person first.": "Bitte zuerst eine Person auswählen.",
        "Super user added successfully!": "Super User erfolgreich hinzugefügt!",
        "This person could not be added.": "Diese Person konnte nicht hinzugefügt werden.",
        "Dismiss": "Verwerfen",
        "Person": "Person",
        "Search by name or email...": "Nach Name oder E-Mail suchen...",
        "Gets the platform area and every platform right. Remove what they should not have on their detail page.": "Erhält den Plattform-Bereich und alle Plattform-Rechte. Nicht benötigte Rechte auf der Detailseite entziehen."
    }
}
</i18n>

<script>
/**
 * Who works this desk, and what they booked in the chosen span.
 *
 * One component for both surfaces: on `/system` the team is the super users, on
 * a shop's account page it is that shop's administrators. The difference is a
 * prop, not a second screen.
 */
import _ from 'lodash';

import { useAuthStore, useSupportTeamStore } from '@/stores';
import { supportTimeFormat } from '@/filters';
import Error from '@/components/Error';
import PaginatedItems from '@/components/PaginatedItems';
import TimespanPicker from '@/components/TimespanPicker';

export default {
    name: 'TeamList',
    components: {
        Error,
        'paginated-items': PaginatedItems,
        'timespan-picker': TimespanPicker,
    },
    props: {
        // 'system' = our own desk, 'cms' = one shop's own team.
        scope: {
            type: String,
            default: 'system',
        },
        cmsId: {
            type: [String, Number],
            default: null,
        },
    },
    data() {
        return {
            loading: true,
            loadError: null,
            promote: {
                visible: false,
                userId: undefined,
                candidates: [],
                searching: false,
                saving: false,
                error: '',
            },
        };
    },
    computed: {
        mayManageSuperUsers() {
            return useAuthStore().platformRightKeys.indexOf('platform:super-users') !== -1;
        },
        supportTeamStore() {
            return useSupportTeamStore();
        },
        teamPaginated() {
            return this.supportTeamStore.teamPaginated;
        },
        isSystemScope() {
            return this.scope === 'system';
        },
        desk() {
            return this.isSystemScope ? 'platform' : 'tenant';
        },
        paginationType() {
            return this.isSystemScope ? 'tckt_support_team_system' : 'tckt_support_team_cms';
        },
        span: {
            get() {
                return this.supportTeamStore.span;
            },
            set(value) {
                this.supportTeamStore.defineSpan(value);
                this.supportTeamStore.defineCurrentPage(1);
                this.fetch();
            },
        },
        showFilters() {
            return _.get(this.teamPaginated, 'showFilters') || false;
        },
        hasFilters() {
            return !_.isEmpty(this.teamPaginated.filters);
        },
        sort() {
            const variables = this.teamPaginated.variables;
            const defaultSort = this.teamPaginated.defaultSort || { field: 'logged_time', direction: 'desc' };
            const order = variables.order !== undefined && variables.order !== '' ? variables.order : defaultSort.field;
            const direction =
                variables.direction !== undefined && variables.direction !== ''
                    ? variables.direction
                    : defaultSort.direction;

            return { order, direction };
        },
    },
    created() {
        // The picker sets an opening span on create; only fetch once it has.
        if (this.span) this.fetch();
    },
    methods: {
        openPromoteDialog() {
            this.promote.error = '';
            this.promote.userId = undefined;
            this.promote.candidates = [];
            this.promote.visible = true;
        },
        candidateLabel(item) {
            const name = `${_.get(item, 'first_name') || ''} ${_.get(item, 'last_name') || ''}`.trim();

            return name ? `${name} · ${item.email}` : item.email;
        },
        searchCandidates(term) {
            if (!term || term.length < 2) {
                this.promote.candidates = [];

                return;
            }

            this.promote.searching = true;

            return this.supportTeamStore
                .searchSuperUserCandidates(term)
                .then((items) => {
                    this.promote.candidates = items;
                })
                .catch(() => {
                    this.promote.candidates = [];
                })
                .finally(() => {
                    this.promote.searching = false;
                });
        },
        confirmPromote() {
            // Never a greyed out button: the click says which field is missing.
            if (!this.promote.userId) {
                this.promote.error = this.$t('Choose a person first.');

                return Promise.resolve();
            }

            this.promote.error = '';
            this.promote.saving = true;

            return this.supportTeamStore
                .savePlatformTeamMember({ userId: this.promote.userId, member: { super_user: true } })
                .then(() => {
                    this.promote.visible = false;

                    return this.fetch();
                })
                .then(() => {
                    this.$notify.success({
                        title: this.$t('Super user added successfully!'),
                        iconClass: 'fas el-icon-fa-check',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                })
                .catch((error) => {
                    this.$notify.error({
                        title: this.$t('An error occurred!'),
                        message: _.get(error, 'message') || this.$t('This person could not be added.'),
                        iconClass: 'fas el-icon-fa-xmark',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                })
                .finally(() => {
                    this.promote.saving = false;
                });
        },
        fetch() {
            if (!this.span) return Promise.resolve();

            this.loading = true;

            this.loadError = null;

            return this.supportTeamStore
                .fetchTeamPaginated({ desk: this.desk, cmsId: this.cmsId })
                .catch((error) => {
                    // Being refused and failing to load are different things
                    // and must not read the same.
                    this.loadError = /forbidden/i.test(_.get(error, 'message') || '')
                        ? { code: 403, message: this.$t('You are not allowed to see this overview.') }
                        : { code: 500, message: this.$t('The team could not be loaded.') };
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        empty(key) {
            return { key, match: '', like: '', from: '', to: '', in: [] };
        },
        handleSizeChange(perPage) {
            this.supportTeamStore.defineItemsPerPage(perPage);
            this.supportTeamStore.defineCurrentPage(1);
            this.fetch();
        },
        handleCurrentChange(currentPage) {
            this.supportTeamStore.defineCurrentPage(currentPage);
            this.fetch();
        },
        handleColsShow(value) {
            this.supportTeamStore.defineFilterVisibility(value);
        },
        handleFilterInputChange(key, value, debounce = true) {
            this.supportTeamStore.defineFilterValue({ key, value });
            this.supportTeamStore.defineCurrentPage(1);

            if (debounce) {
                this.debouncedFetch();
                return;
            }

            this.fetch();
        },
        handleSortInputChange(key, value) {
            this.supportTeamStore.defineSort({ key, value });
            this.fetch();
        },
        clearFilters() {
            Object.keys(this.teamPaginated.filters || {}).forEach((key) => {
                this.supportTeamStore.defineFilterValue({ key, value: this.empty(key) });
            });

            this.supportTeamStore.defineCurrentPage(1);
            this.fetch();
        },
        toggleFilterVisiblity() {
            this.supportTeamStore.defineFilterVisibility(!this.showFilters);
        },
        handleClick(item) {
            this.$router.push(
                this.isSystemScope
                    ? { name: 'system-team-detail', params: { id: item.id } }
                    : { name: 'account-team-member', params: { id: this.cmsId, userId: item.id } }
            );
        },
        formatSupportTime(seconds) {
            return supportTimeFormat(seconds);
        },
        formatMinutes(minutes) {
            return supportTimeFormat((minutes || 0) * 60);
        },
        debouncedFetch: _.debounce(function debouncedFetch() {
            this.fetch();
        }, 400),
    },
};
</script>

<style lang="sass" scoped>
.table-top
    flex-wrap: wrap
    row-gap: 0.5rem

    .el-button + .el-button
        margin-left: 0

    .left
        display: flex
        flex-wrap: wrap
        align-items: center
        gap: 0.375rem

    .right
        display: flex
        flex-wrap: wrap
        align-items: center
        justify-content: flex-end
        gap: 0.375rem

.team-list
    .muted
        color: #909399
</style>
